// Shared domain enums and types — single source of truth across api / web / mobile.

export const ROLES = ["owner", "admin", "member"] as const;
export type Role = (typeof ROLES)[number];

export const REGIONS = ["us", "eu", "uk", "multi"] as const;
export type Region = (typeof REGIONS)[number];

export const CLINICAL_MODELS = ["ai_only", "clinician_review"] as const;
export type ClinicalModel = (typeof CLINICAL_MODELS)[number];

export const BILLING_STATUS = ["trialing", "active", "past_due", "delinquent"] as const;
export type BillingStatus = (typeof BILLING_STATUS)[number];

// Doctor persona specialties (extendable).
export const SPECIALTIES = [
  "general_medicine",
  "urgent_care",
  "mental_health",
  "dermatology",
  "pediatrics",
  "cardiology",
  "endocrinology",
  "womens_health",
] as const;
export type Specialty = (typeof SPECIALTIES)[number];

// Consultation lifecycle.
export const CONSULT_STATUS = [
  "booked",
  "intake",
  "ready",
  "in_progress",
  "completed",
  "summarized",
  "signed_off",
  "cancelled",
] as const;
export type ConsultStatus = (typeof CONSULT_STATUS)[number];

export const REPORT_TYPES = ["lab", "imaging", "dicom", "note", "other"] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export const REPORT_STATUS = ["uploaded", "processing", "processed", "failed"] as const;
export type ReportStatus = (typeof REPORT_STATUS)[number];

export const JOURNAL_ENTRY_TYPES = [
  "consult",
  "report",
  "vitals",
  "medication",
  "allergy",
  "condition",
  "note",
] as const;
export type JournalEntryType = (typeof JOURNAL_ENTRY_TYPES)[number];

export const CONSENT_TYPES = ["telehealth", "recording", "data_processing", "sharing"] as const;
export type ConsentType = (typeof CONSENT_TYPES)[number];

// Triage acuity, lowest → highest. "emergency" hard-triggers escalation.
export const TRIAGE_LEVELS = ["routine", "non_urgent", "urgent", "emergency"] as const;
export type TriageLevel = (typeof TRIAGE_LEVELS)[number];

export const REVIEW_STATUS = [
  "not_required",
  "pending",
  "signed_off",
  "amended",
  "rejected",
] as const;
export type ReviewStatus = (typeof REVIEW_STATUS)[number];

// Vitals metrics with the units the platform normalizes to.
export const VITAL_METRICS = [
  "heart_rate",
  "spo2",
  "bp_systolic",
  "bp_diastolic",
  "temp_c",
  "resp_rate",
  "glucose",
] as const;
export type VitalMetric = (typeof VITAL_METRICS)[number];

export const VITAL_SOURCES = ["ble", "healthkit", "googlefit", "manual"] as const;
export type VitalSource = (typeof VITAL_SOURCES)[number];

// ---------------------------------------------------------------------------
// Structured AI / clinical content (persisted as jsonb on the relevant tables)
// ---------------------------------------------------------------------------

// One conversational turn captured from the Tavus session.
export interface TranscriptTurn {
  role: "doctor" | "patient" | "system";
  text: string;
  at?: string; // ISO timestamp
}

// A single normalized lab value extracted from a report.
export interface LabValue {
  name: string; // e.g. "Hemoglobin"
  loinc?: string; // LOINC code where mappable
  value: string; // raw value as reported
  numeric?: number; // parsed numeric value where possible
  unit?: string;
  referenceRange?: string;
  abnormal?: boolean;
  flag?: "low" | "high" | "critical" | "normal";
}

// Structured findings produced by the ingestion pipeline for one report.
// Imaging output is intentionally framed as observations, never a diagnosis.
export interface ClinicalFindings {
  reportType: ReportType;
  summary: string; // short factual summary of what the document contains
  labValues?: LabValue[];
  abnormalValues?: LabValue[];
  imagingObservations?: string[]; // "observations for clinician review", not a read
  medicationsMentioned?: string[];
  allergiesMentioned?: string[];
  conditionsMentioned?: string[];
  documentDate?: string; // date on the document, if found
  // Always record provenance so downstream statements can be cited.
  sourceReportId?: string;
  confidence?: "low" | "medium" | "high";
}

// Concise brief assembled before the call from reports + journal.
export interface PreVisitBrief {
  chiefComplaint: string;
  relevantHistory: string[];
  activeMedications: string[];
  allergies: string[];
  abnormalFindings: { finding: string; sourceReportId?: string }[];
  recentVitals?: { metric: VitalMetric; value: number; unit: string; at: string }[];
  risks: string[]; // risk factors / red flags to watch for
  suggestedFocus: string[]; // what the doctor should explore
  generatedAt: string;
}

// SOAP-structured clinical note for the chart.
export interface SoapNote {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

// Patient- and chart-facing visit outputs.
export interface VisitSummary {
  patientFriendly: string; // plain-language explanation for the patient
  keyPoints: string[];
  recommendations: string[]; // next steps / self-care
  referrals: string[];
  followUp?: string;
  // Every clinical statement should reference its evidence source.
  citations: { statement: string; sourceReportId?: string }[];
  disclaimer: string;
}

export interface RedFlag {
  code: string; // e.g. "chest_pain", "spo2_below_90", "suicidal_ideation"
  detail: string;
  source: "symptom" | "vital" | "report" | "transcript";
  severity: "warn" | "critical";
  at?: string;
}

// Triage / red-flag assessment attached to a consultation.
export interface TriageAssessment {
  level: TriageLevel;
  rationale: string;
  redFlags: RedFlag[];
  recommendEmergencyServices: boolean;
  escalateToClinician: boolean;
  assessedAt: string;
}
